import {
  toWSDateString,
  WSCard,
  WSElement,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { DataViewGrid } from "../../../shared/components/DataView";
import {
  RequirementsListView,
  selectorRequirementsListView
} from "../selectors/selectorRequirementsListView";
import {
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import {
  getIsCustomRequirement,
  getIsGeneralRequirement,
  getRequirementListKey
} from "../utils";

import { RequirementsEmptyState } from "./RequirementsEmptyState";

type RequirementPreviewListProps = {
  requirements: Array<ExtendedRequirementTableItemData>;
  contextType: RequirementContextType;
  hideTaxVerificationConsent?: boolean;
  getOnViewDetails: (
    item: PayeeRequirement,
    additionalData?: RequirementAdditionalData
  ) => ((item: PayeeRequirement) => void) | undefined;
  /**
   * The *list* read is still in flight. Without it an unresolved list is
   * indistinguishable from an empty one and this renders "No requirements
   * active" over a request that has not returned.
   */
  isLoading?: boolean;
};

/** How many placeholder cards to stand in for an unresolved list. */
const LOADING_CARDS_COUNT = 3;

export const ExtendedRequirementPreviewList: React.FC<
  RequirementPreviewListProps
> = ({
  requirements,
  contextType,
  getOnViewDetails,
  hideTaxVerificationConsent,
  isLoading
}) => {
  const requirementsConfig = useRequirementsConfig({
    requirements: requirements.filter(
      requirement => !getIsCustomRequirement(requirement)
    ) as RequirementTableItemData[],
    contextType,
    context: {
      hideTaxVerificationConsent
    }
  });

  const listView = selectorRequirementsListView({
    count: requirements.length,
    isLoading: !!isLoading
  });

  // Placeholder cards while the list resolves — the grid view has no table
  // skeleton to fall back on, so it stands in its own card shapes.
  if (listView === RequirementsListView.Loading) {
    return (
      <WSList>
        {Array.from({ length: LOADING_CARDS_COUNT }).map((_, index) => (
          <WSCard key={`loading-${index}`}>
            <WSList gap="M">
              <WSElement shimmer>
                <WSText.Heading5>Loading requirement</WSText.Heading5>
              </WSElement>
              <WSElement shimmer>
                <WSText.ParagraphSm>
                  Loading requirement details
                </WSText.ParagraphSm>
              </WSElement>
            </WSList>
          </WSCard>
        ))}
      </WSList>
    );
  }

  // Only "no requirements" once the list has actually resolved.
  if (listView === RequirementsListView.Empty) {
    return <RequirementsEmptyState />;
  }

  return (
    <WSList>
      {requirements.map((requirement, index) => {
        const isCustomRequirement = getIsCustomRequirement(requirement);
        const isGeneralRequirement =
          !isCustomRequirement && getIsGeneralRequirement(requirement);
        const isRegularRequirement =
          !isCustomRequirement && !isGeneralRequirement;

        const title = isCustomRequirement ? (
          requirement.nameCell.text || ""
        ) : (
          <WSList.Horizontal alignItems="center" gap="S">
            {requirementsConfig.getNameCellProps(requirement).text}
            {requirementsConfig.getNameCellProps(requirement).secondaryText}
          </WSList.Horizontal>
        );

        return (
          <WSCard key={getRequirementListKey(requirement, index)}>
            <WSList gap="M">
              <WSElement>
                <WSSectionToolbar
                  actions={
                    isCustomRequirement
                      ? requirement.menuActions
                      : requirementsConfig.getToolbarActions(requirement)
                  }
                  button={
                    !getIsCustomRequirement(requirement) && isRegularRequirement
                      ? {
                          label: "View",
                          onClick: () =>
                            getOnViewDetails(
                              requirement,
                              requirementsConfig.getAdditionalData(requirement)
                            )?.(requirement)
                        }
                      : undefined
                  }
                  mb="NONE"
                  title={title as string}
                />
                {!isCustomRequirement && (
                  <WSText.ParagraphXs color="gray500">
                    {/* TODO(v3-verify): v1 `updatedAt` has no v3 twin on
                    PayeeRequirement (only per-event timestamps under
                    `events`). Using `events.createdAt` as the nearest
                    available "last touched" signal per migration guidance;
                    this reads as instance-created rather than last-updated. */}
                    Updated at {toWSDateString(requirement.events?.createdAt)}
                  </WSText.ParagraphXs>
                )}
              </WSElement>
              <DataViewGrid
                data={
                  isCustomRequirement
                    ? [
                        {
                          icon: requirement.typeCell.icon,
                          label: "Type",
                          value: requirement.typeCell.text,
                          secondaryText: requirement.typeCell.secondaryText
                        },
                        {
                          label: {
                            text: "Status"
                          },
                          value: "",
                          hideValue: true,
                          pill: requirement.statusCell.pill,
                          secondaryText: requirement.statusCell.secondaryText
                        }
                      ]
                    : requirementsConfig.getRequirementBasicInformation(
                        requirement
                      ) || []
                }
                gridColumns={3}
              />
              {!getIsCustomRequirement(requirement) && isRegularRequirement
                ? requirementsConfig.getRequirementDetailsProgressWidget(
                    requirement,
                    false
                  )
                : undefined}
            </WSList>
          </WSCard>
        );
      })}
    </WSList>
  );
};
